import {useQuery, type UseQueryResult} from '@github-ui/react-query'
import {useParams} from '@github-ui/react-router'
import type {AgentTaskAppPayload} from '../types/agent-task-app-payload'
import {maybeNormalizeSession} from '../types/api-task'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useInitializeAuthToken} from './use-initialize-auth-token'
import {useIntentDetectionContext, type IntentDetectionTaskCreationRequest} from '../contexts/IntentDetectionContext'
import {createTask} from './use-create-api-task'
import {CopilotChatMessageStreamer} from '@github-ui/copilot-chat/utils/copilot-chat-message-streamer'
import type {StreamingChatMessage} from '../types/session'
import {useChatStreamContext, type StreamingState} from '../contexts/ChatStreamContext'

async function fetchAppPayloadByTask(
  incomingTaskID: string | undefined,
  owner: string | undefined,
  repo: string | undefined,
  taskCreationRequest: IntentDetectionTaskCreationRequest | null,
  setStreamingChatResponse?: (sessionId: string, response: string | null) => void,
  setStreamingState?: (state: StreamingState) => void,
): Promise<AgentTaskAppPayload | null> {
  if (!owner) {
    throw new Error('No owner provided')
  }

  if (!repo) {
    throw new Error('No repo provided')
  }

  let taskID = incomingTaskID
  // new isn't a valid UUID, so this signals a new task start
  // If we use a static key, we get stale data when returning to chat
  if (!taskID || taskID === 'new') {
    if (taskCreationRequest) {
      const trimmedContent = taskCreationRequest.problemStatement.trim()
      setStreamingState?.('requested')

      let resp: Response
      try {
        resp = await createTask({
          nwo: taskCreationRequest.nwo,
          problemStatement: trimmedContent,
          baseRef: taskCreationRequest.baseRef,
          eventType: 'chat_page',
          eventContent: trimmedContent,
          model: taskCreationRequest.model,
          agentId: taskCreationRequest.agentId,
          customAgent: taskCreationRequest.customAgent,
          createPullRequest: taskCreationRequest.createPullRequest,
          clientGeneratedId: taskCreationRequest.clientGeneratedId,
          intentDetectionMode: taskCreationRequest.mode,
          endpoint: `/agents/repos/${taskCreationRequest.nwo}/tasks/chat`,
        })
      } catch (error) {
        setStreamingState?.('closed')
        throw error
      }

      const contentType = resp.headers.get('Content-Type') ?? ''

      if (contentType.includes('text/event-stream')) {
        // sync_llm route: parse SSE stream to extract task ID and chat content
        const reader = resp.body?.getReader()
        if (!reader) {
          setStreamingState?.('closed')
          throw new Error('No reader found in response body')
        }

        const streamer = new CopilotChatMessageStreamer<StreamingChatMessage>(reader)
        const stream = streamer.stream()
        const iterator = stream[Symbol.asyncIterator]()

        // Consume messages until we find the metadata message
        let sessionID = ''
        let result = await iterator.next()
        while (!result.done) {
          if (result.value.type === 'metadata') {
            taskID = result.value.task_id
            sessionID = result.value.session_id
            break
          }
          result = await iterator.next()
        }

        // Drain remaining messages in the background
        void (async () => {
          let accumulated = ''
          try {
            for (let next = await iterator.next(); !next.done; next = await iterator.next()) {
              if (next.value.type === 'content') {
                const content = next.value.body
                if (content && setStreamingChatResponse) {
                  accumulated += content
                  setStreamingChatResponse(sessionID, accumulated)
                }
              }
            }
          } finally {
            setStreamingState?.('closed')
          }
        })()
      } else {
        // async_agent route: parse JSON response to extract task ID
        setStreamingState?.('closed')
        const data = await resp.json()
        // Support both response shapes: { task: { id } } (legacy) and { id } (new REST style)
        taskID = data.task?.id ?? data.id
      }
    } else {
      // Already in flight so skip it
      return null
    }
  }

  if (!taskID) {
    throw new Error('Task ID could not be determined')
  }

  const url = new URL(`/copilot/t/${owner}/${repo}/${taskID}`, window.location.origin)
  return fetchTaskPayload(url.toString(), taskID)
}

// Shared helper that fetches a task payload URL, validates the response, and normalizes sessions.
async function fetchTaskPayload(url: string, taskID: string): Promise<AgentTaskAppPayload> {
  const response = await fetch(url, {
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch task payload for ${taskID}`)
  }

  const data = await response.json()
  if (data.sessions) {
    data.sessions = data.sessions.map(maybeNormalizeSession)
  }
  return data
}

function hasExpectedKeys<T extends Record<string, unknown>>(data: unknown, keys: Array<keyof T>): data is T {
  return typeof data === 'object' && data !== null && keys.every(key => key in data)
}

// Fetch task payload directly by task ID, without requiring owner/repo.
// Used for repo-less tasks at /copilot/tasks/:taskID.
async function fetchAppPayloadByTaskId(taskID: string): Promise<AgentTaskAppPayload | null> {
  const url = new URL(`/copilot/tasks/${taskID}`, window.location.origin)
  return fetchTaskPayload(url.toString(), taskID)
}

const agentTaskKeys: Array<keyof AgentTaskAppPayload> = [
  'pull',
  'sessions',
  'currentUser',
  'currentUserCanPush',
  'steeringEnabled',
  'logsPollingInterval',
  'sessionsPollingInterval',
]

function isTaskAppPayload(payload: unknown): payload is AgentTaskAppPayload {
  return !!payload && hasExpectedKeys<AgentTaskAppPayload>(payload, agentTaskKeys)
}

type UrlParams = {
  globalID?: string
  taskID?: string
  owner?: string
  repository?: string
}

export function useOrFetchOrCreateTaskPayload(): UseQueryResult<AgentTaskAppPayload | null> {
  const ssrPayload = useAppPayload<AgentTaskAppPayload>()
  const {taskID, owner, repository} = useParams<UrlParams>()
  const payloadMatchesUrl = !taskID || ssrPayload?.taskId === taskID
  const payloadIsValid = isTaskAppPayload(ssrPayload) && (ssrPayload.pull || ssrPayload.taskId) && payloadMatchesUrl
  const validPayload = payloadIsValid
    ? {...ssrPayload, sessions: ssrPayload.sessions.map(maybeNormalizeSession)}
    : undefined

  const {taskCreationRequest} = useIntentDetectionContext()
  const {setStreamingChatResponse, setStreamingState} = useChatStreamContext()

  // Initialize auth token from SSR payload to avoid double token fetching
  useInitializeAuthToken(ssrPayload?.initialAuthToken)

  // For repo-less tasks (e.g. /copilot/tasks/:taskID), we don't have owner/repository
  const isRepolessTask = !!taskID && !owner && !repository

  const queryResponse = useQuery<AgentTaskAppPayload | null>({
    // We key off the client generated ID rather thant he whole task creation request as it's unchanged once created
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ['create-task-payload', taskID, owner, repository, taskCreationRequest?.clientGeneratedId],
    queryFn: () => {
      if (isRepolessTask) {
        return fetchAppPayloadByTaskId(taskID)
      }
      return fetchAppPayloadByTask(
        taskID,
        owner,
        repository,
        taskCreationRequest,
        setStreamingChatResponse,
        setStreamingState,
      )
    },
    initialData: validPayload,
    enabled: !!taskID && !validPayload,
    staleTime: Infinity,
  })

  return queryResponse
}
